import { AnswerFormat, AssessmentSection, StudentAssessmentOutcome } from 'types';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { Form, Formik } from 'formik';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  useCreateStudentAssessmentOutcomeStore,
  useUpdateStudentAssessmentOutcomeStore,
} from 'graph/state/query-hooks/use-student-assessment-outcomes';
import QuestionForm from './QuestionForm';
import CharacterChartForm from './CharacterChartForm';

const t = (key) => translate('ui.assessments', key);
const QuestionSection = (props: {
  studentId: number;
  assessmentId: number;
  section: AssessmentSection;
  initialValues: any;
  validationSchema: any;
  outcomes?: StudentAssessmentOutcome[];
  submit: any;
  back: any;
}): React.ReactElement => {
  const createStudentAssessmentOutcome = useCreateStudentAssessmentOutcomeStore();
  const updateStudentAssessmentOutcome = useUpdateStudentAssessmentOutcomeStore();
  const classes = useStyles();

  const firstQuestion =
    props.section.assessmentSectionQuestions.length < 1
      ? null
      : props.section.assessmentSectionQuestions[0].assessmentQuestion;
  const characterChartQuestion =
    firstQuestion && firstQuestion.answerType.format === AnswerFormat.CharacterChart;

  async function _handleSubmit(values: any, actions: any) {
    for (const questionId of props.section.assessmentSectionQuestions.map(
      (question) => question.assessmentQuestion.id
    )) {
      const existingOutcome = props.outcomes?.find(
        (o) => o.assessmentQuestionId === questionId && o.studentId === props.studentId
      );

      if (existingOutcome) {
        if (existingOutcome.answerTypeOptionId != values[questionId]) {
          await updateStudentAssessmentOutcome({
            variables: {
              where: {
                studentAssessmentOutcomeId: {
                  studentId: props.studentId,
                  assessmentId: props.assessmentId,
                  assessmentSectionId: props.section.id,
                  assessmentQuestionId: questionId,
                },
              },
              data: {
                answerTypeOptionId: parseInt(values[questionId]),
              },
            },
          });
        }
      } else if (values[questionId]) {
        await createStudentAssessmentOutcome({
          variables: {
            data: {
              studentId: props.studentId,
              assessmentId: props.assessmentId,
              assessmentSectionId: props.section.id,
              assessmentQuestionId: questionId,
              answerTypeOptionId: parseInt(values[questionId]),
            },
          },
        });
      }
    }
    actions.setSubmitting(false);
    props.submit();
  }

  async function setAllNotObserved() {
    for (const questionId of props.section.assessmentSectionQuestions.map(
      (question) => question.assessmentQuestion.id
    )) {
      const existingOutcome = props.outcomes?.find(
        (o) => o.assessmentQuestionId === questionId && o.studentId === props.studentId
      );

      if (existingOutcome) {
        if (existingOutcome.answerTypeOptionId != null) {
          await updateStudentAssessmentOutcome({
            variables: {
              where: {
                studentAssessmentOutcomeId: {
                  studentId: props.studentId,
                  assessmentId: props.assessmentId,
                  assessmentSectionId: props.section.id,
                  assessmentQuestionId: questionId,
                },
              },
              data: {
                answerTypeOptionId: null,
              },
            },
          });
        }
      } else if (questionId === firstQuestion?.id) {
        // FIXME - this sucks - can't save with null optionId, which means can't distinguish first time visit vs skipped
        // so we're creating and then updating the first question so a record exists for this section
        await createStudentAssessmentOutcome({
          variables: {
            data: {
              studentId: props.studentId,
              assessmentId: props.assessmentId,
              assessmentSectionId: props.section.id,
              assessmentQuestionId: questionId,
              answerTypeOptionId: firstQuestion.answerType.options[0].id,
            },
          },
        });
        await updateStudentAssessmentOutcome({
          variables: {
            where: {
              studentAssessmentOutcomeId: {
                studentId: props.studentId,
                assessmentId: props.assessmentId,
                assessmentSectionId: props.section.id,
                assessmentQuestionId: questionId,
              },
            },
            data: {
              answerTypeOptionId: null,
            },
          },
        });
      }
    }

    props.submit();
  }

  return (
    <>
      <Typography variant="h2" component="h2" className={classes.sectionHeader}>
        {props.section.title}
      </Typography>
      <Formik
        initialValues={props.initialValues}
        validationSchema={props.validationSchema}
        onSubmit={_handleSubmit}
        enableReinitialize={true}
      >
        {({ isSubmitting }) => (
          <Form id={props.section.id.toString()} style={{ flexGrow: 1 }}>
            {isSubmitting && <CircularProgress className={classes.centered} size={24} />}
            {characterChartQuestion && (
              <CharacterChartForm section={props.section} isSubmitting={isSubmitting} />
            )}
            {!characterChartQuestion && (
              <QuestionForm section={props.section} isSubmitting={isSubmitting} />
            )}
            <div className={classes.footerGroup}>
              <div>
                {characterChartQuestion && (
                  <Button variant="outlined" disabled={isSubmitting} onClick={setAllNotObserved}>
                    {t('skipItem')}
                  </Button>
                )}
                <Button variant="outlined" disabled={isSubmitting} onClick={props.back}>
                  {t('back')}
                </Button>
                <Button variant="contained" disabled={isSubmitting} type="submit" color="primary">
                  {t('next')}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default QuestionSection;

const useStyles = makeStyles((theme: Theme) => {
  return {
    row: {
      display: 'flex',
      flexFlow: 'row wrap',
    },
    sectionHeader: {
      color: theme.palette.primary.main,
    },
    formSection: {
      marginTop: theme.spacing(4),

      '& .MuiButton-outlinedPrimary': {
        float: 'right',
        marginBottom: theme.spacing(4),
      },
    },
    footerGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      '& .MuiButton-root': {
        marginLeft: theme.spacing(2),
      },
    },
    loading: {
      opacity: 0.4,
    },
    centered: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginRight: '-50%',
    },
    thumbnail: {
      border: `1px solid ${theme.palette.black.main}`,
      padding: theme.spacing(1),
    },
    btnScoring: {
      display: 'flex',
      alignItems: 'center',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2),

      '& p': {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        paddingRight: theme.spacing(2),
        margin: 0,
      },
    },
    borderYes: {
      border: '2px solid #098e51',
    },
    borderSomewhat: {
      border: '2px solid #6c820c',
    },
    borderNo: {
      border: '2px solid #e86825',
    },
    borderNotObserved: {
      border: '2px solid #0c72b7',
    },
  };
});
